import { useEffect, useMemo, useState } from "react";
import {
  Type, X, RotateCcw, Smartphone, Link2, ShieldCheck, ExternalLink,
  AlertTriangle, CheckCircle2, Info, Sparkles, Download, Cloud, CloudOff, RefreshCw,
  Share2, Undo2, History, Wand2, Copy, Check,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  PRESETS,
  applyState,
  loadStored,
  saveStored,
  clearAllManagedVars,
  syncToGlassCardHeader,
  runA11yChecks,
  downloadExport,
  loadFromProfile,
  saveToProfile,
  buildShareLink,
  pushHistory,
  popHistory,
  loadHistory,
  loadLastSaved,
  markLastSaved,
  validateTokens,
  applyTokenFix,
  applyAllTokenFixes,
  loadFallbackMode,
  saveFallbackMode,
  applyFallbackMode,
  FALLBACK_META,
  type Preset,
  type StoredState,
  type FallbackMode,
} from "@/lib/typography";
import { TypographyWizard } from "./TypographyWizard";

export function TypographyTogglePanel() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"presets" | "mobile" | "a11y" | "fallback" | "share">("presets");
  const [state, setState] = useState<StoredState>({ preset: "default" });
  const [wizardOpen, setWizardOpen] = useState(false);
  const [syncStatus, setSyncStatus] = useState<"idle" | "syncing" | "ok" | "error">("idle");
  const [syncMessage, setSyncMessage] = useState<string>("");
  const [shareLink, setShareLink] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [fallback, setFallback] = useState<FallbackMode>("auto");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const profile = await loadFromProfile();
      if (cancelled) return;
      const next = profile ?? loadStored() ?? { preset: "default" };
      setState(next);
      applyState(next);
      saveStored(next);
      pushHistory(next, "init");
      markLastSaved(next);

      const fb = loadFallbackMode();
      setFallback(fb);
      await applyFallbackMode(fb);
    })();
    return () => { cancelled = true; };
  }, []);

  const persist = (next: StoredState, label?: string) => {
    setState(next);
    saveStored(next);
    applyState(next);
    pushHistory(next, label);
    setHistoryVersion((n) => n + 1);
  };

  const choosePreset = (p: Preset) =>
    persist({ ...state, preset: p.name, customVars: undefined }, `preset:${p.name}`);

  const reset = () => {
    clearAllManagedVars();
    try { localStorage.removeItem("vzl_banner_typo_v2"); } catch {/* noop */}
    persist({ preset: "default" }, "reset");
  };

  const setMobileVar = (key: keyof NonNullable<StoredState["mobileVars"]>, val: string) => {
    persist({ ...state, mobileVars: { ...(state.mobileVars ?? {}), [key]: val } }, "mobile-edit");
  };

  const handleSync = () => {
    const res = syncToGlassCardHeader();
    if (!res.ok) return alert(res.reason ?? "Sync failed");
    persist({ ...state, customVars: { ...(state.customVars ?? {}), ...res.applied } }, "sync-glass");
  };

  const handleSaveToProfile = async () => {
    setSyncStatus("syncing");
    const res = await saveToProfile(state);
    if (res.ok) {
      markLastSaved(state);
      setSyncStatus("ok");
      setSyncMessage("Saved to your profile.");
      setTimeout(() => setSyncStatus("idle"), 2500);
    } else {
      setSyncStatus("error");
      setSyncMessage(res.error ?? "Failed to save");
    }
  };

  const handleLoadFromProfile = async () => {
    setSyncStatus("syncing");
    const profile = await loadFromProfile();
    if (profile) {
      persist(profile, "pull-profile");
      markLastSaved(profile);
      setSyncStatus("ok");
      setSyncMessage("Loaded from profile.");
    } else {
      setSyncStatus("error");
      setSyncMessage("No profile preset found.");
    }
    setTimeout(() => setSyncStatus("idle"), 2500);
  };

  const handleUndo = () => {
    // pop current; previous is now the tail
    popHistory();
    const list = loadHistory();
    const prev = list[list.length - 1];
    if (!prev) return;
    setState(prev.state);
    saveStored(prev.state);
    applyState(prev.state);
    setHistoryVersion((n) => n + 1);
  };

  const handleRestoreLastSaved = () => {
    const last = loadLastSaved();
    if (!last) return alert("No 'last saved' snapshot yet. Use Save to profile first.");
    persist(last, "restore-last-saved");
  };

  const handleBuildShare = async () => {
    const url = buildShareLink(state);
    setShareLink(url);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {/* noop */}
  };

  const handleFallbackChange = async (mode: FallbackMode) => {
    setFallback(mode);
    saveFallbackMode(mode);
    await applyFallbackMode(mode);
  };

  const a11y = useMemo(
    () => (open && tab === "a11y" ? runA11yChecks() : []),
    [open, tab, state]
  );
  const tokenIssues = useMemo(
    () => (open && tab === "a11y" ? validateTokens() : []),
    [open, tab, state]
  );

  const history = useMemo(() => loadHistory(), [historyVersion, open]);
  const canUndo = history.length >= 2;

  const enabled =
    import.meta.env.DEV ||
    (typeof window !== "undefined" && window.location.search.includes("devtypo=1"));
  if (!enabled) return null;

  const currentMobile = state.mobileVars ?? {};

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="fixed bottom-4 right-4 z-[9999] flex items-center gap-1.5 rounded-full border border-white/10 bg-black/70 px-3 py-2 text-[11px] text-white/80 backdrop-blur-xl shadow-lg hover:bg-black/85 hover:text-white transition"
        aria-label="Toggle typography preview"
      >
        <Type className="h-3.5 w-3.5" />
        <span className="font-medium">Typo</span>
      </button>

      {open && (
        <div className="fixed bottom-16 right-4 z-[9999] w-[340px] max-h-[80vh] overflow-y-auto rounded-2xl border border-white/10 bg-[hsl(240_8%_8%/0.95)] backdrop-blur-2xl shadow-2xl p-3 text-white/85">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60">
              Sidebar Typography
            </div>
            <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* History controls */}
          <div className="grid grid-cols-2 gap-1.5 mb-2">
            <button
              onClick={handleUndo}
              disabled={!canUndo}
              className="flex items-center justify-center gap-1.5 text-[11px] rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] py-1.5 transition disabled:opacity-40"
              title={canUndo ? "Undo last typography change" : "No previous change in history"}
            >
              <Undo2 className="h-3 w-3" />
              Undo last
            </button>
            <button
              onClick={handleRestoreLastSaved}
              className="flex items-center justify-center gap-1.5 text-[11px] rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] py-1.5 transition"
              title="Restore the last preset you saved to profile"
            >
              <History className="h-3 w-3" />
              Restore saved
            </button>
          </div>

          <button
            onClick={() => setWizardOpen(true)}
            className="w-full mb-2 flex items-center justify-center gap-1.5 text-[11px] rounded-lg border border-[hsl(268_90%_65%/0.4)] bg-[hsl(268_90%_65%/0.12)] hover:bg-[hsl(268_90%_65%/0.2)] text-white py-2 font-medium transition"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Open guided wizard
          </button>

          <div className="grid grid-cols-5 gap-0.5 rounded-lg bg-white/[0.04] p-0.5 mb-3">
            {([
              ["presets", "Set", Type],
              ["mobile", "Mob", Smartphone],
              ["a11y", "A11y", ShieldCheck],
              ["fallback", "Fall", Wand2],
              ["share", "Share", Share2],
            ] as const).map(([key, label, Icon]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex items-center justify-center gap-1 text-[10px] py-1.5 rounded-md transition ${
                  tab === key ? "bg-[hsl(268_90%_65%/0.18)] text-white" : "text-white/55 hover:text-white"
                }`}
              >
                <Icon className="h-3 w-3" />
                {label}
              </button>
            ))}
          </div>

          {tab === "presets" && (
            <div className="flex flex-col gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.name}
                  onClick={() => choosePreset(p)}
                  className={`text-left text-xs rounded-lg border px-2.5 py-2 transition ${
                    state.preset === p.name
                      ? "border-[hsl(268_90%_65%/0.45)] bg-[hsl(268_90%_65%/0.12)] text-white"
                      : "border-white/8 bg-white/[0.02] hover:bg-white/[0.06]"
                  }`}
                >
                  <div className="font-medium">{p.label}</div>
                  <div className="text-[10px] text-white/45 mt-0.5">
                    label: {p.vars["--font-banner-label"]?.replace("var(--font-", "").replace(")", "")} · item:{" "}
                    {p.vars["--font-banner-item"]?.replace("var(--font-", "").replace(")", "")}
                  </div>
                </button>
              ))}

              <div className="border-t border-white/8 my-2" />
              <button
                onClick={handleSync}
                className="flex items-center justify-center gap-1.5 text-[11px] rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] py-2 transition"
              >
                <Link2 className="h-3 w-3" />
                Sync to glass card header
              </button>
              <button
                onClick={() => downloadExport(state)}
                className="flex items-center justify-center gap-1.5 text-[11px] rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] py-2 transition"
              >
                <Download className="h-3 w-3" />
                Export JSON
              </button>

              <div className="grid grid-cols-2 gap-1.5 mt-1">
                <button
                  onClick={handleSaveToProfile}
                  disabled={syncStatus === "syncing"}
                  className="flex items-center justify-center gap-1.5 text-[11px] rounded-lg border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 text-emerald-200 py-2 transition disabled:opacity-50"
                >
                  <Cloud className="h-3 w-3" />
                  Save to profile
                </button>
                <button
                  onClick={handleLoadFromProfile}
                  disabled={syncStatus === "syncing"}
                  className="flex items-center justify-center gap-1.5 text-[11px] rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] py-2 transition disabled:opacity-50"
                >
                  <RefreshCw className={`h-3 w-3 ${syncStatus === "syncing" ? "animate-spin" : ""}`} />
                  Pull profile
                </button>
              </div>
              {syncMessage && (
                <div className={`flex items-center gap-1.5 text-[10px] mt-1 ${
                  syncStatus === "error" ? "text-rose-400" : "text-emerald-300"
                }`}>
                  {syncStatus === "error" ? <CloudOff className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                  {syncMessage}
                </div>
              )}

              <Link
                to="/dev/typography"
                className="flex items-center justify-center gap-1.5 text-[11px] rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] py-2 transition mt-1"
              >
                <ExternalLink className="h-3 w-3" />
                Open diagnostics
              </Link>
              <button
                onClick={reset}
                className="flex items-center justify-center gap-1.5 text-[11px] text-white/55 hover:text-white/85 py-1.5"
              >
                <RotateCcw className="h-3 w-3" />
                Reset to project default
              </button>
            </div>
          )}

          {tab === "mobile" && (
            <div className="space-y-3">
              <p className="text-[10px] text-white/45 leading-relaxed">
                Overrides apply only at <code className="text-white/65">≤ 640px</code>. Empty = inherit desktop.
              </p>
              {[
                { key: "--banner-label-size-mobile" as const, label: "Label size", min: 9, max: 16, step: 0.5, unit: "px" },
                { key: "--banner-label-tracking-mobile" as const, label: "Label tracking", min: 0, max: 0.25, step: 0.01, unit: "em" },
                { key: "--banner-item-size-mobile" as const, label: "Item size", min: 11, max: 18, step: 0.5, unit: "px" },
                { key: "--banner-item-tracking-mobile" as const, label: "Item tracking", min: -0.03, max: 0.05, step: 0.005, unit: "em" },
              ].map((row) => {
                const raw = currentMobile[row.key] ?? "";
                const num = parseFloat(raw) || 0;
                return (
                  <div key={row.key}>
                    <div className="flex items-center justify-between text-[11px] mb-1">
                      <span className="text-white/70">{row.label}</span>
                      <span className="font-mono text-white/60">{raw || "inherit"}</span>
                    </div>
                    <input
                      type="range"
                      min={row.min}
                      max={row.max}
                      step={row.step}
                      value={num}
                      onChange={(e) => setMobileVar(row.key, `${e.target.value}${row.unit}`)}
                      className="w-full accent-[hsl(268_90%_65%)]"
                    />
                  </div>
                );
              })}
              <button
                onClick={() => persist({ ...state, mobileVars: undefined }, "mobile-clear")}
                className="w-full text-[11px] text-white/55 hover:text-white py-1.5"
              >
                Clear mobile overrides
              </button>
            </div>
          )}

          {tab === "a11y" && (
            <div className="space-y-3">
              {/* Token-level validator */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-white/55">Token validator</div>
                  {tokenIssues.length > 0 && (
                    <button
                      onClick={() => persist(applyAllTokenFixes(state, tokenIssues), "fix-all-tokens")}
                      className="text-[10px] rounded-md border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 px-2 py-0.5"
                    >
                      Fix all
                    </button>
                  )}
                </div>
                {tokenIssues.length === 0 ? (
                  <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-2 text-[11px] text-emerald-200 flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    All tokens within readable ranges.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {tokenIssues.map((iss) => (
                      <div key={iss.token} className={`rounded-lg border p-2 ${
                        iss.severity === "error" ? "border-rose-500/40 bg-rose-500/5" : "border-amber-500/40 bg-amber-500/5"
                      }`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <code className="text-[10px] font-mono text-white/80 truncate block">{iss.token}</code>
                            <div className="text-[10px] text-white/55 mt-0.5">{iss.reason}</div>
                            <div className="text-[10px] mt-1 font-mono">
                              <span className="text-rose-300">{iss.currentValue}</span>
                              <span className="text-white/40 mx-1">→</span>
                              <span className="text-emerald-300">{iss.suggestedValue}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => persist(applyTokenFix(state, iss), `fix:${iss.token}`)}
                            className="text-[10px] rounded-md border border-white/10 bg-white/[0.05] hover:bg-white/[0.1] text-white/85 px-2 py-1 shrink-0"
                          >
                            Fix
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-white/8 pt-2">
                <div className="text-[10px] uppercase tracking-wider text-white/55 mb-1.5">General checks</div>
                <div className="space-y-1.5">
                  {a11y.map((iss) => {
                    const Icon =
                      iss.severity === "error" || iss.severity === "warn" ? AlertTriangle :
                      iss.id === "ok" ? CheckCircle2 : Info;
                    const color =
                      iss.severity === "error" ? "text-rose-400" :
                      iss.severity === "warn" ? "text-amber-400" : "text-emerald-400";
                    return (
                      <div key={iss.id} className="rounded-lg border border-white/8 bg-white/[0.02] p-2">
                        <div className={`flex items-start gap-2 text-[11px] ${color}`}>
                          <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <div className="text-white/85">{iss.message}</div>
                            {iss.suggestion && <div className="text-white/55 mt-0.5">{iss.suggestion}</div>}
                            {iss.presetSuggestion && (
                              <button
                                onClick={() => {
                                  const p = PRESETS.find((pp) => pp.name === iss.presetSuggestion);
                                  if (p) choosePreset(p);
                                }}
                                className="mt-1 text-[10px] underline text-[hsl(268_90%_75%)] hover:text-white"
                              >
                                Apply safer preset: {iss.presetSuggestion}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {tab === "fallback" && (
            <div className="space-y-2">
              <p className="text-[10px] text-white/45 leading-relaxed">
                Behavior if Display / Body / Mono webfonts fail to load.
              </p>
              {(Object.keys(FALLBACK_META) as FallbackMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => handleFallbackChange(mode)}
                  className={`w-full text-left rounded-lg border px-2.5 py-2 transition ${
                    fallback === mode
                      ? "border-[hsl(268_90%_65%/0.45)] bg-[hsl(268_90%_65%/0.12)]"
                      : "border-white/8 bg-white/[0.02] hover:bg-white/[0.06]"
                  }`}
                >
                  <div className="text-xs font-medium">{FALLBACK_META[mode].label}</div>
                  <div className="text-[10px] text-white/55 mt-0.5">{FALLBACK_META[mode].description}</div>
                </button>
              ))}
              <Link
                to="/dev/typography"
                className="flex items-center justify-center gap-1.5 text-[11px] rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] py-2 transition mt-1"
              >
                <ExternalLink className="h-3 w-3" />
                Re-run diagnostics to verify
              </Link>
            </div>
          )}

          {tab === "share" && (
            <div className="space-y-2">
              <p className="text-[10px] text-white/45 leading-relaxed">
                Encodes the current preset, custom overrides, mobile overrides, and glass-sync mapping into a URL.
              </p>
              <button
                onClick={handleBuildShare}
                className="w-full flex items-center justify-center gap-1.5 text-[11px] rounded-lg border border-[hsl(268_90%_65%/0.4)] bg-[hsl(268_90%_65%/0.12)] hover:bg-[hsl(268_90%_65%/0.2)] text-white py-2 transition"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
                {copied ? "Copied!" : "Create share link"}
              </button>
              {shareLink && (
                <div className="rounded-lg border border-white/10 bg-black/40 p-2">
                  <div className="text-[9px] uppercase tracking-wider text-white/45 mb-1">Shareable URL</div>
                  <div className="text-[10px] font-mono text-white/75 break-all max-h-24 overflow-y-auto">
                    {shareLink}
                  </div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                    className="mt-1.5 w-full flex items-center justify-center gap-1 text-[10px] rounded-md border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] py-1"
                  >
                    <Copy className="h-3 w-3" /> Copy again
                  </button>
                </div>
              )}
              {history.length > 0 && (
                <div className="border-t border-white/8 pt-2 mt-2">
                  <div className="text-[10px] uppercase tracking-wider text-white/55 mb-1">Recent changes ({history.length})</div>
                  <div className="space-y-0.5 max-h-32 overflow-y-auto">
                    {history.slice().reverse().slice(0, 8).map((h, i) => (
                      <div key={i} className="text-[10px] text-white/55 font-mono flex items-center justify-between">
                        <span className="truncate">{h.label ?? h.state.preset}</span>
                        <span className="text-white/30 shrink-0">{new Date(h.at).toLocaleTimeString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 text-[10px] text-white/35 leading-relaxed">
            Saved per browser. Visible in dev or with{" "}
            <code className="text-white/55">?devtypo=1</code>.
          </div>
        </div>
      )}

      <TypographyWizard
        open={wizardOpen}
        initialState={state}
        onClose={() => setWizardOpen(false)}
        onApply={(next) => { persist(next, "wizard-apply"); markLastSaved(next); }}
      />
    </>
  );
}
